'use client';

import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';

/**
 * Global Rate Limit Overlay
 *
 * Intercepts 429 responses and shows a fullscreen overlay with countdown.
 * Automatically reloads when the rate limit resets.
 */
export default function RateLimitOverlay() {
  const [isLimited, setIsLimited] = useState(false);
  const [resetTime, setResetTime] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [limitInfo, setLimitInfo] = useState({ limit: null, used: null });

  // Intercept fetch responses - only for OWN backend 429s
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (response.status === 429) {
        // Determine the request URL
        const requestUrl = typeof args[0] === 'string'
          ? args[0]
          : args[0]?.url || '';

        // Only intercept our own backend 429s (/api/* paths)
        // Ignore external API 429s (OpenAI, Stripe, etc.)
        const isOwnBackend = requestUrl.startsWith('/api/');

        // Double-check via response header: our backend adds X-RateLimit-Source
        const rateLimitSource = response.headers.get('x-ratelimit-source');
        const isXoffiSource = rateLimitSource === 'xoffi-backend';

        if (!isOwnBackend && !isXoffiSource) {
          console.debug('[RateLimit] External 429 ignored for:', requestUrl);
          return response;
        }

        // Get reset time from standardized headers
        const retryAfter = response.headers.get('retry-after');
        const rateLimitReset = response.headers.get('x-ratelimit-reset');

        // Get limit info from headers
        const limit = response.headers.get('x-ratelimit-limit');
        const remaining = response.headers.get('x-ratelimit-remaining');

        let used = null;
        if (limit) {
          const limitNum = parseInt(limit);
          const remainingNum = remaining ? parseInt(remaining) : 0;
          used = limitNum - remainingNum;
          setLimitInfo({ limit: limitNum, used });
        }

        let resetTimestamp;
        if (rateLimitReset) {
          resetTimestamp = parseInt(rateLimitReset) * 1000; // Convert to ms
        } else if (retryAfter) {
          resetTimestamp = Date.now() + (parseInt(retryAfter) * 1000);
        } else {
          resetTimestamp = Date.now() + 60000; // Default 60s
        }

        setResetTime(resetTimestamp);
        setIsLimited(true);
        console.warn('[RateLimit] Backend 429 detected, reset at:', new Date(resetTimestamp), 'Limit:', limit, 'Used:', used);
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!isLimited || !resetTime) return;

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((resetTime - Date.now()) / 1000));
      setSecondsRemaining(remaining);

      if (remaining <= 0) {
        // Auto-reload when limit resets
        console.log('[RateLimit] Limit reset, reloading...');
        window.location.reload();
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [isLimited, resetTime]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Manual reload
  const handleManualReload = useCallback(() => {
    window.location.reload();
  }, []);

  if (!isLimited) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-slate-900 border border-yellow-500/50 rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-4 rounded-full bg-yellow-500/20 border border-yellow-500/30">
            <AlertTriangle className="w-12 h-12 text-yellow-400" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-white mb-2">
          Rate Limit erreicht
        </h2>

        <p className="text-gray-400 mb-4">
          Zu viele Anfragen. Die Seite wird automatisch neu geladen.
        </p>

        {/* Limit Info */}
        {limitInfo.limit && (
          <div className="bg-slate-800/30 rounded-lg px-4 py-2 mb-6 inline-block">
            <span className="text-yellow-400 font-mono font-semibold">{limitInfo.used}</span>
            <span className="text-gray-500 mx-1">/</span>
            <span className="text-gray-400 font-mono">{limitInfo.limit}</span>
            <span className="text-gray-500 text-sm ml-2">Anfragen</span>
          </div>
        )}

        {/* Countdown */}
        <div className="bg-slate-800/50 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Clock className="w-6 h-6 text-yellow-400" />
            <span className="text-4xl font-mono font-bold text-yellow-400">
              {formatTime(secondsRemaining)}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            bis zum automatischen Reload
          </p>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-1000"
            style={{
              width: `${Math.max(0, 100 - (secondsRemaining / ((resetTime - Date.now() + secondsRemaining * 1000) / 1000) * 100))}%`
            }}
          />
        </div>

        {/* Manual reload button */}
        <button
          onClick={handleManualReload}
          className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Jetzt neu laden
        </button>

        <p className="text-xs text-gray-600 mt-4">
          Tipp: Warte auf den automatischen Reload um das Limit nicht erneut zu triggern.
        </p>
      </div>
    </div>
  );
}
